import { SelectCardOption } from '@foxera/feature/clients/interfaces/select-card-option.interface';
import { IWorkoutPlanResponse, IWorkoutPlanUpdateRequest } from '@foxera/shared/interfaces/client.interface';

/**
 * Helper method to map backend constants to SelectCardOption interface
 * @param backendData - The data from backend (e.g., FitnessLevel, Goal)
 * @param constantsMap - The constants object with icon and description
 */
export function mapToSelectCardOptions(
  backendData: Record<string, string>,
  constantsMap: Record<string, { icon: string; description?: string }>
): SelectCardOption[] {
  return Object.entries(backendData).map(([backendKey, displayValue]) => {
    const constantData = constantsMap[backendKey];
    
    return {
      value: backendKey,
      label: displayValue,
      icon: constantData?.icon,
      description: constantData?.description
    } as SelectCardOption;
  });
}

/**
 * Helper method to map frontend constants to SelectCardOption interface
 * For constants that don't come from backend but are defined in the frontend
 * @param constantsMap - The constants object with label, icon, and optional description
 * @returns Array of SelectCardOption
 */
export function mapConstantToSelectCardOptions(
  constantsMap: Record<string, { label: string; icon?: string; description?: string }>
): SelectCardOption[] {
  return Object.entries(constantsMap).map(([key, value]) => ({
    value: key,
    label: value.label,
    icon: value.icon,
    description: value.description
  }));
}

/**
 * Build the payload for updating a workout plan (PUT request)
 * Extracts only the necessary fields: day, structure title, durationInMinutes, exerciseId, and order
 * @param plan - The modified workout plan response
 * @param isReviewed - Optional review status to update
 * @returns The update request payload with minimal structure
 */
export function buildWorkoutPlanUpdatePayload(plan: IWorkoutPlanResponse, isReviewed?: boolean): IWorkoutPlanUpdateRequest {
  return {
    isReviewed: isReviewed !== undefined ? isReviewed : plan.isReviewed,
    days: plan.workoutPlan.days.map((d) => ({
      day: d.day,
      title: d.title,
      structure: d.structure.map((s) => ({
        title: s.title,
        durationInMinutes: s.durationInMinutes,
        exercises: (s.exercises ?? [])
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((ex) => ({
            exerciseId: ex.id,
            order: ex.order,
            totalTimeSec: (ex.totalTimeSec && ex.totalTimeSec > 0) ? ex.totalTimeSec : null,
            totalReps: (ex.totalReps && ex.totalReps > 0) ? ex.totalReps : null,
          })),
      })),
    })),
  };
}

/**
 * Deep clone a workout plan response object
 * Uses JSON serialization for simple, safe cloning of plain data objects
 * @param plan - The workout plan to clone
 * @returns A new, independent copy of the workout plan
 */
export function deepCloneWorkoutPlan(plan: IWorkoutPlanResponse): IWorkoutPlanResponse {
  return JSON.parse(JSON.stringify(plan)) as IWorkoutPlanResponse;
}
